import {
  InternalRefetchQueryDescriptor,
  useApolloClient,
} from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { ErrorMessage } from "@msys/ui";
import { Box, Button } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useSnackbar } from "notistack";
import {
  RfItemAttachmentsBox_ItemFragment,
  RfItemAttachmentsBoxDocument,
  RfItemAttachmentsBoxQueryVariables,
  useRfItemAttachmentsAddMutation,
  useRfItemAttachmentsBoxQuery,
  useRfItemAttachmentsGetUploadUrlMutation,
  useRfRemoveItemAttachmentMutation,
  useSetRfItemAttachmentClientVisibilityMutation,
} from "./RfQuoteItemAttachmentsBox.generated.js";
import { DocType, PermissionName } from "../../../../clients/graphqlTypes.js";
import { RestrictedByDocumentPermissionByTypeWithDebug } from "../../../auth/RestrictedByDocumentPermissionByType.js";
import { RfItemFileBox } from "./RfItemFileBox.js";
import { makeRefetchQuery } from "../../../../clients/apollo-client-refetch-queries.js";
import { useUserData } from "../../../auth/useUserData.js";
import { useRemoveItemAttachmentMutation } from "../../attachments/Attachments.generated.js";
import { ItemFilesBox_ItemFragment } from "../../doc-items/boxes/ItemFilesBox.generated.js";

interface Props {
  projectId: string | null;
  docId: string;
  docType: DocType;
  viewerPermissions: PermissionName[];
  item: RfItemAttachmentsBox_ItemFragment & ItemFilesBox_ItemFragment;
  isEditable?: boolean;
  isInitiallyClosed?: boolean;
  onUpdateDataRefetchQueries?: InternalRefetchQueryDescriptor[];
}

export const RfQuoteItemAttachmentsBox = ({
  projectId,
  docId,
  docType,
  viewerPermissions,
  item,
  isEditable = true,
  isInitiallyClosed,
  onUpdateDataRefetchQueries,
}: Props) => {
  const { t } = useTranslate(["SapS4Hana", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();
  const viewer = useUserData().currentUser!;
  const [refreshFailureMessage, setRefreshFailureMessage] = React.useState<
    string | null
  >(null);
  // Set from the start of a post-write refresh until it succeeds or fails, so
  // no second write starts against stale attachment or mapping data.
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const { data, loading, error, refetch } = useRfItemAttachmentsBoxQuery({
    client,
    variables: { projectId: projectId!, docId: docId, itemId: item.id },
    skip: !projectId,
  });

  const sapAttachmentResult = getDataOrNull(data?.rfGetItemAttachments);
  const metadataError =
    error ??
    (!loading && !sapAttachmentResult
      ? new Error(
          t("Something went wrong. Please try again.", { ns: "Global" })
        )
      : undefined);
  const sapAttachments = sapAttachmentResult?.attachments ?? [];
  const sapAttachmentsById = React.useMemo(
    () =>
      new Map(sapAttachments.map(attachment => [attachment.id, attachment])),
    [sapAttachments]
  );
  const attachments = React.useMemo(
    () =>
      item.attachments.map(attachment => ({
        ...attachment,
        ...sapAttachmentsById.get(attachment.id),
        isProductAttachment: attachment.isProductAttachment,
        clientVisibility: attachment.clientVisibility,
      })),
    [item.attachments, sapAttachmentsById]
  );

  const [getUploadUrl] = useRfItemAttachmentsGetUploadUrlMutation({
    client,
  });
  const [addAttachments] = useRfItemAttachmentsAddMutation({ client });
  const [removeAttachment] = useRfRemoveItemAttachmentMutation({ client });
  const [setClientVisibility] = useSetRfItemAttachmentClientVisibilityMutation({
    client,
  });
  const [removeGenericAttachment] = useRemoveItemAttachmentMutation({ client });

  const setAttachmentClientVisibility = React.useCallback(
    async (attachmentId: string, clientVisibility: boolean) => {
      await setClientVisibility({
        variables: {
          input: {
            projectId,
            docId,
            itemId: item.id,
            attachmentId,
            clientVisibility,
          },
        },
        refetchQueries: [
          ...(onUpdateDataRefetchQueries ?? []),
          makeRefetchQuery<RfItemAttachmentsBoxQueryVariables>({
            document: RfItemAttachmentsBoxDocument,
            variables: {
              projectId: projectId!,
              docId: docId,
              itemId: item.id,
            },
          }),
        ],
        awaitRefetchQueries: true,
      });
    },
    [docId, item.id, projectId, setClientVisibility, onUpdateDataRefetchQueries]
  );

  // Mirrors the document-permission guard on the box's other write controls.
  const canUpdateDocument =
    docType !== "QUOTE" || viewerPermissions.includes("quote_document_update");
  const canChangeVisibility =
    canUpdateDocument && item.authorOrganisationId === viewer.organisation.id;

  const refreshAttachments = React.useCallback(
    async (successMessage: string) => {
      setIsRefreshing(true);
      try {
        const descriptors = onUpdateDataRefetchQueries ?? [];
        const namedQueries = descriptors.flatMap(descriptor =>
          typeof descriptor === "string" || !("query" in descriptor)
            ? [descriptor]
            : []
        );
        const explicitQueries = descriptors.flatMap(descriptor =>
          typeof descriptor === "object" && "query" in descriptor
            ? [descriptor]
            : []
        );
        const metadataRefetch = refetch();
        await Promise.all([
          ...(namedQueries.length > 0
            ? [client.refetchQueries({ include: namedQueries })]
            : []),
          ...explicitQueries.map(query =>
            client.query({ ...query, fetchPolicy: "network-only" })
          ),
          metadataRefetch,
        ]);
        // A permission or capability result resolves without an Apollo error
        // but leaves the metadata unresolved, so it must not clear the failure.
        const { data: refreshed } = await metadataRefetch;
        if (!getDataOrNull(refreshed?.rfGetItemAttachments)) {
          throw new Error("Attachment metadata is unresolved after refresh");
        }
        setRefreshFailureMessage(null);
      } catch (error) {
        console.error("Attachment refresh error:", error);
        setRefreshFailureMessage(successMessage);
      } finally {
        setIsRefreshing(false);
      }
    },
    [client, onUpdateDataRefetchQueries, refetch]
  );

  const handleAdd = React.useCallback(
    async (files: File[]) => {
      try {
        // Get upload URL
        const uploadPromises = files.map(async file => {
          const { data: urlData } = await getUploadUrl({
            variables: {
              input: {
                docId: docId,
                itemId: item.id,
                filename: file.name,
                contentType: file.type || "application/octet-stream",
              },
            },
          });

          if (!urlData?.rfGetItemAttachmentUploadUrl) {
            throw new Error(`Failed to get upload URL for ${file.name}`);
          }

          const { uploadUrl, persistedUri } =
            urlData.rfGetItemAttachmentUploadUrl;

          // Upload to S3
          const response = await fetch(uploadUrl, {
            method: "PUT",
            body: file,
            headers: {
              "Content-Type": file.type || "application/octet-stream",
              "Content-Disposition": `attachment; filename="${file.name}"`,
            },
          });

          if (!response.ok) {
            throw new Error(`Failed to upload ${file.name}`);
          }

          return {
            title: file.name,
            mimeType: file.type || "application/octet-stream",
            url: persistedUri,
            docType: "",
          };
        });

        const attachmentsData = await Promise.all(uploadPromises);

        await addAttachments({
          variables: {
            input: {
              projectId: projectId!,
              docId: docId,
              itemId: item.id,
              attachments: attachmentsData,
            },
          },
        });
      } catch (error) {
        console.error("Upload error:", error);
        enqueueSnackbar(
          error instanceof Error
            ? error.message
            : t("Failed to upload file", { ns: "SapS4Hana" }),
          { variant: "error" }
        );
        return;
      }
      enqueueSnackbar(t("File uploaded successfully", { ns: "SapS4Hana" }), {
        variant: "success",
      });
      await refreshAttachments(
        t("File uploaded successfully", { ns: "SapS4Hana" })
      );
    },
    [
      projectId,
      docId,
      item.id,
      getUploadUrl,
      addAttachments,
      enqueueSnackbar,
      t,
      refreshAttachments,
    ]
  );

  const handleDeleteCombined = React.useCallback(
    async (attachmentId: string) => {
      const attachment = item.attachments.find(({ id }) => id === attachmentId);
      const isSapAttachment =
        attachment?.group === "sap-s4hana" ||
        sapAttachmentsById.has(attachmentId);

      try {
        if (isSapAttachment && !attachment?.isProductAttachment) {
          await removeAttachment({
            variables: {
              input: {
                projectId: projectId!,
                docId,
                itemId: item.id,
                attachmentId,
              },
            },
          });
        } else {
          const result = await removeGenericAttachment({
            variables: { attachmentId, itemId: item.id },
          });
          if (!result.data?.removeItemAttachments.ok) {
            throw new Error(
              t("Something went wrong. Please try again.", { ns: "Global" })
            );
          }
        }
      } catch (error) {
        console.error("Delete error:", error);
        enqueueSnackbar(
          error instanceof Error ? error.message : String(error),
          { variant: "error" }
        );
        return;
      }
      enqueueSnackbar(t("Changes saved", { ns: "Global" }), {
        variant: "success",
      });
      await refreshAttachments(t("Changes saved", { ns: "Global" }));
    },
    [
      docId,
      enqueueSnackbar,
      item.attachments,
      item.id,
      projectId,
      refreshAttachments,
      removeAttachment,
      removeGenericAttachment,
      sapAttachmentsById,
      t,
    ]
  );

  return (
    <Box
      sx={{
        "& .MuiListItem-root:has(.sap-doc-type-dropdown)": {
          pr: 2,
          "& .MuiListItemSecondaryAction-root": {
            position: "relative",
            transform: "none",
            right: "auto",
          },
        },
        "& .MuiListItem-root:has(.sap-doc-type-dropdown) .MuiListItemButton-root":
          {
            pr: 2,
          },
      }}
    >
      {refreshFailureMessage && (
        <ErrorMessage
          message={`${refreshFailureMessage}. ${t("Something went wrong. Please try again.", { ns: "Global" })}`}
          button={
            <Button
              onClick={() => void refreshAttachments(refreshFailureMessage)}
              variant="outlined"
            >
              {t("Refresh", { ns: "Global" })}
            </Button>
          }
        />
      )}
      {!refreshFailureMessage && metadataError && (
        <ErrorMessage
          message={metadataError.message}
          button={
            <Button
              onClick={() => {
                void refetch().catch(() => undefined);
              }}
              variant="outlined"
            >
              {t("Retry", { ns: "Global" })}
            </Button>
          }
        />
      )}
      <RfItemFileBox
        projectId={projectId}
        docId={docId}
        itemId={item.id}
        title={t("Attachments", { ns: "SapS4Hana" })}
        attachments={attachments}
        isInitiallyClosed={isInitiallyClosed}
        showImagesInGallery={false}
        readOnly={!isEditable || !!refreshFailureMessage}
        loading={loading || isRefreshing}
        handleAdd={handleAdd}
        handleDelete={handleDeleteCombined}
        metadataResolved={
          !loading && !isRefreshing && !metadataError && !refreshFailureMessage
        }
        onDocTypeChange={async () => {
          enqueueSnackbar(t("Changes saved", { ns: "Global" }), {
            variant: "success",
          });
          await refreshAttachments(t("Changes saved", { ns: "Global" }));
        }}
        setClientVisibility={
          canChangeVisibility ? setAttachmentClientVisibility : undefined
        }
        renderAddButton={button => (
          <RestrictedByDocumentPermissionByTypeWithDebug
            permissionByType={{
              QUOTE: "quote_document_update",
            }}
            document={{ docType, viewerPermissions }}
          >
            {button}
          </RestrictedByDocumentPermissionByTypeWithDebug>
        )}
        renderDeleteButton={button => (
          <RestrictedByDocumentPermissionByTypeWithDebug
            permissionByType={{
              QUOTE: "quote_document_update",
            }}
            document={{ docType, viewerPermissions }}
          >
            {button}
          </RestrictedByDocumentPermissionByTypeWithDebug>
        )}
        renderDocTypeDropdown={dropdown => (
          <RestrictedByDocumentPermissionByTypeWithDebug
            permissionByType={{
              QUOTE: "quote_document_update",
            }}
            document={{ docType, viewerPermissions }}
          >
            {dropdown}
          </RestrictedByDocumentPermissionByTypeWithDebug>
        )}
      />
    </Box>
  );
};
